import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useOptionsBasket } from './OptionsBasketContext';
import OptionsBasketPanel from './OptionsBasketPanel';
import './pages/portfolio_theme.css';
import './OptionsBasketDrawer.css';

const SHELL_WIDTH = 1040;
const SHELL_HEIGHT = 560;

// Centered-ish default spawn, clamped so it's never off-screen on a narrow
// viewport — same idea as OrderWindow's DEFAULT_POSITION.
const getDefaultPosition = () => ({
    x: Math.max(12, (window.innerWidth - Math.min(SHELL_WIDTH, window.innerWidth * 0.96)) / 2),
    y: Math.max(12, (window.innerHeight - Math.min(SHELL_HEIGHT, window.innerHeight * 0.85)) / 2),
});

// Thin floating-window chrome around the shared OptionsBasketPanel (leg
// list, margins, place) — reachable via the floating basket button from any
// chain page. Draggable and non-blocking (no backdrop) rather than a modal:
// staging a leg here is meant to happen alongside still browsing/adding from
// whatever page the basket was opened from (e.g. clicking "Add to Cart" on
// more closed positions), not instead of it — so it needs to be movable out
// of the way rather than pinning the user behind a dimmed overlay.
// The toast lives here (not in the panel) because this component is always
// mounted at the App root, so it can still surface a toast fired while the
// drawer itself is closed (e.g. an "Added to basket" notification from
// AddContractModal on some other page).
const OptionsBasketDrawer = () => {
    const { isDrawerOpen, setIsDrawerOpen, toast } = useOptionsBasket();

    const [position, setPosition] = useState(getDefaultPosition);
    const [isDragging, setIsDragging] = useState(false);
    const dragStateRef = useRef(null);

    useEffect(() => {
        if (isDrawerOpen) setPosition(getDefaultPosition());
    }, [isDrawerOpen]);

    const handleDragMove = (e) => {
        const drag = dragStateRef.current;
        if (!drag) return;
        setPosition({
            x: Math.min(Math.max(drag.originX + (e.clientX - drag.startX), -SHELL_WIDTH + 80), window.innerWidth - 80),
            y: Math.min(Math.max(drag.originY + (e.clientY - drag.startY), 0), window.innerHeight - 40),
        });
    };

    const handleDragEnd = () => {
        dragStateRef.current = null;
        setIsDragging(false);
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
    };

    // Delegated rather than a dedicated handle prop on OptionsBasketPanel —
    // that component is shared with the plain inline /order-basket page,
    // which has no business being draggable. Only starts a drag when the
    // mousedown lands on the header itself, not the close button/checkbox/
    // title icon inside it.
    const handleShellMouseDown = (e) => {
        if (!e.target.closest('.opt-panel-header')) return;
        if (e.target.closest('button, input, label')) return;
        dragStateRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            originX: position.x,
            originY: position.y,
        };
        setIsDragging(true);
        window.addEventListener('mousemove', handleDragMove);
        window.addEventListener('mouseup', handleDragEnd);
    };

    useEffect(() => () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
    }, []);

    // Esc closes the panel — Ctrl/Cmd+Enter (place the basket) is handled
    // inside OptionsBasketPanel itself, so it works the same way whether the
    // panel is shown here (the drawer) or inline on the /order-basket page.
    useEffect(() => {
        if (!isDrawerOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setIsDrawerOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isDrawerOpen, setIsDrawerOpen]);

    return (
        <>
            {isDrawerOpen && (
                <div
                    className={`opt-drawer-shell ${isDragging ? 'dragging' : ''}`}
                    style={{ left: position.x, top: position.y }}
                    onMouseDown={handleShellMouseDown}
                >
                    <OptionsBasketPanel onClose={() => setIsDrawerOpen(false)} />
                </div>
            )}

            {toast.show && (
                <div className={`opt-basket-notification ${toast.type}`}>
                    {toast.type === 'success' ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                    <span>{toast.message}</span>
                </div>
            )}
        </>
    );
};

export default OptionsBasketDrawer;
